import { Eye, FileText } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import type { TopUpRequest } from "@/types/wallet";
import { formatDate, formatINR, formatTime, TOPUP_STATUS_META } from "@/pages/retailer/wallet/data";
import ReceiptPreviewModal from "./ReceiptPreviewModal";

interface TopupDetailModalProps {
  open: boolean;
  onClose: () => void;
  request: TopUpRequest | null;
}

export default function TopupDetailModal({ open, onClose, request }: TopupDetailModalProps) {
  const [receiptOpen, setReceiptOpen] = useState(false);
  if (!request) return null;

  const status = TOPUP_STATUS_META[request.status];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Top-up request details"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-ink-secondary">{request.requestId}</p>
          <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${status.bg} ${status.text}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {status.label}
          </span>
        </div>

        <div className="flex flex-col divide-y divide-line rounded-[10px] border border-line">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Retailer</span>
            <span className="text-right text-sm font-medium text-ink">
              {request.shopName || request.retailerName || "Retailer"}
              {(request.userCode || request.mobile) && (
                <span className="block text-xs text-ink-secondary">{[request.userCode, request.mobile].filter(Boolean).join(" · ")}</span>
              )}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Amount</span>
            <span className="text-right text-sm font-semibold text-ink">{formatINR(request.amount)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Payment Mode</span>
            <span className="text-right text-sm font-medium text-ink">{request.paymentMode}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Reference Number</span>
            <span className="font-mono text-sm text-ink">{request.referenceNumber}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Payment Date</span>
            <span className="text-right text-sm font-medium text-ink">{formatDate(request.requestedAt)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Requested On</span>
            <span className="text-right text-sm font-medium text-ink">
              {formatDate(request.requestedAt)} · {formatTime(request.requestedAt)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Actioned On</span>
            <span className="text-right text-sm font-medium text-ink">
              {request.actionedAt ? `${formatDate(request.actionedAt)} · ${formatTime(request.actionedAt)}` : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Admin Remark</span>
            <span className="text-right text-sm text-ink">{request.adminRemark || "—"}</span>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-ink">Receipt</label>
          <div className="flex items-center gap-3 rounded-[10px] border border-line bg-slate-50/60 p-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-primary-500">
              <FileText className="h-4 w-4" />
            </span>
            <span className="flex-1 truncate text-[13px] text-ink">{request.receiptFileName}</span>
            <Button variant="ghost" size="sm" leftIcon={<Eye className="h-4 w-4" />} onClick={() => setReceiptOpen(true)}>
              View Receipt
            </Button>
          </div>
        </div>
      </div>

      <ReceiptPreviewModal
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        request={request}
      />
    </Modal>
  );
}